import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type Row,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import {
  Fragment,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { cn } from "../cn";
import { type Density, useDensity } from "../density";
import { EmptyState } from "../empty-state";
import { Icon } from "../icon";
import { Skeleton } from "../skeleton";
import { cellClasses, rowClasses } from "./density-classes";

// The one data table of the console. It is a real `<table>` (TanStack Table v8 only computes rows, sorting and visibility; this file
// renders). Keyboard users get the same controls as a pointer user: a row can be clickable, but it must still hold a real link or button
// (`onRowClick` is a convenience for the pointer, never the only way in). Under 720 px of table width each row becomes a card: the cells
// carry `data-label`, which a CSS `::before` shows. The table roles are written out so the card layout keeps its table meaning for a
// screen reader (a browser may drop the role when `display` changes).
//
// PRIVACY: `data-label` and `caption` hold the fixed header words of the table. They must never hold customer text (a message, a memory
// value, a note): attributes are copied to error reports.

declare module "@tanstack/react-table" {
  // The type parameters are required by the declaration being merged.
  interface ColumnMeta<TData, TValue> {
    /** The text of the column in the card layout when `header` is not a string. A fixed word, never customer text. */
    label?: string;
    /** Classes for the cells of this column (for example `text-right tabular-nums`). */
    className?: string;
    /** Classes for the header cell of this column. */
    headClassName?: string;
    /** The cells of this column name their row: a `<th scope="row">` (role `rowheader`) instead of a `<td>`. Use it for the first column. */
    rowHeader?: boolean;
  }
}

export type DataTableProps<TData> = Readonly<{
  // `any` is TanStack's own default for the value type of a column; a typed column list is assignable to it.
  // biome-ignore lint/suspicious/noExplicitAny: the value type differs per column
  columns: readonly ColumnDef<TData, any>[];
  data: readonly TData[];
  /** A stable id of a row (the id of the record). Never the index. */
  getRowId: (row: TData) => string;
  /** The name of the table. Required: it is the `<caption>`. A fixed phrase, never customer text. */
  caption: string;
  /** Show the caption as a visible title. By default it is for screen readers only (the page has its own heading). */
  captionVisible?: boolean;
  /** Overrides the density the staff member chose in the shell. */
  density?: Density;
  isLoading?: boolean;
  /** How many skeleton rows to show while `isLoading`. */
  loadingRows?: number;
  /** Shown instead of the rows when `data` is empty. */
  emptyState?: ReactNode;
  /** A convenience for the pointer. The row must still hold a real link or button for the keyboard. */
  onRowClick?: (row: TData) => void;
  /** Extra `data-*` attributes of a row (for example `{ "data-job": row.id }`): a hook for tests and scripts. Ids and codes only, never customer text. */
  rowAttributes?: (row: TData) => Readonly<Record<`data-${string}`, string | undefined>>;
  /** Extra classes for one row (for example a tint for a row that needs attention). The row must also say it in words: colour is never alone. */
  rowClassName?: (row: TData) => string | undefined;
  /**
   * An extra row under a record, for a panel the person opened (the scores of a search hit). Return `null` for a record with no panel. The `id`
   * is what the opening button names in `aria-controls`. The panel spans every column; in the card layout it is a card of its own.
   */
  renderRowDetail?: (row: TData) => Readonly<{ id: string; content: ReactNode }> | null;
  /** Turns on sorting for the columns that allow it (a column with `enableSorting: false` stays unsorted). */
  sortable?: boolean;
  initialSorting?: SortingState;
  /** Controlled column visibility. Without it the table keeps its own. */
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  /** The header stays visible while the rows scroll. The table then scrolls inside a box of at most `maxHeightClassName`. */
  stickyHeader?: boolean;
  /**
   * The banded table of the redesign: a band behind the column heads, a 16 px frame with a hairline, no shadow, hairline row lines. `true` is
   * the light band (`muted`: Requests, Catalog, Imports, the import review); `"strong"` is the darker band (`pill`: Operations, Staff, Coverage).
   * Without it the head is plain. The head words are always sentence case at 13 px.
   */
  banded?: boolean | "strong";
  maxHeightClassName?: string;
  className?: string;
}>;

// The table roles are written out on purpose. Under 720 px the cells are `display: flex` and the rows `display: block`; Safari and some other
// browsers then drop the table meaning of the elements. An explicit role keeps it. They are passed as props objects, so a linter that
// flags "redundant roles" on native elements does not see the literal (the repetition is the point here).
const roles = {
  table: { role: "table" },
  rowgroup: { role: "rowgroup" },
  row: { role: "row" },
  columnheader: { role: "columnheader" },
  cell: { role: "cell" },
  rowheader: { role: "rowheader" },
} as const;

// A skeleton row is decoration: a screen reader reads the "Loading rows" status instead.
const skeletonRow = { ...roles.row, "aria-hidden": true } as const;

const INTERACTIVE = "a, button, input, select, textarea, summary, label, [role='button']";

function headerLabel<TData>(column: { id: string; columnDef: ColumnDef<TData, unknown> }): string {
  const { header, meta } = column.columnDef;
  if (typeof header === "string") return header;
  return meta?.label ?? column.id;
}

export function DataTable<TData>({
  columns,
  data,
  getRowId,
  caption,
  captionVisible = false,
  density: densityProp,
  isLoading = false,
  loadingRows = 5,
  emptyState,
  onRowClick,
  rowAttributes,
  rowClassName,
  renderRowDetail,
  sortable = false,
  initialSorting,
  columnVisibility: visibilityProp,
  onColumnVisibilityChange,
  stickyHeader = true,
  banded = false,
  maxHeightClassName = "max-h-[75vh]",
  className,
}: DataTableProps<TData>) {
  const chosen = useDensity().density;
  const density = densityProp ?? chosen;
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [ownVisibility, setOwnVisibility] = useState<VisibilityState>({});
  const columnVisibility = visibilityProp ?? ownVisibility;

  const table = useReactTable<TData>({
    data: data as TData[],
    columns: columns as ColumnDef<TData, unknown>[],
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: sortable,
    // TanStack starts a number column descending. Every column starts ascending here, so a click behaves the same everywhere.
    sortDescFirst: false,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: onColumnVisibilityChange ?? setOwnVisibility,
  });

  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleLeafColumns();
  const captionId = useId();
  const scroller = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<{ left: boolean; right: boolean }>({
    left: false,
    right: false,
  });
  // Whether the box really scrolls (sideways, or up and down inside `maxHeightClassName`). Only then is it a focusable region: a table that
  // fits adds no tab stop and no landmark.
  const [scrollable, setScrollable] = useState(false);

  // A visible hint that more columns are off screen: a soft edge on the side that has more. Runs on resize and on scroll.
  const measure = useCallback(() => {
    const node = scroller.current;
    if (node === null) return;
    const left = node.scrollLeft > 1;
    const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
    setOverflow((previous) =>
      previous.left === left && previous.right === right ? previous : { left, right },
    );
    setScrollable(
      node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1,
    );
  }, []);

  useEffect(() => {
    measure();
    const node = scroller.current;
    if (node === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    const content = node.firstElementChild;
    if (content) observer.observe(content);
    return () => observer.disconnect();
  }, [measure]);

  function handleRowClick(event: MouseEvent<HTMLTableRowElement>, row: Row<TData>) {
    if (!onRowClick) return;
    const target = event.target instanceof Element ? event.target : null;
    // A click on the link or the button inside the row does its own job; the row handler is for the empty parts of the row.
    if (target?.closest(INTERACTIVE)) return;
    onRowClick(row.original);
  }

  const cell = cellClasses[density];
  // The look of a column head: its size, and the band of a banded table. Decided once here, not for every head.
  const headClasses = cn(
    "text-[13px]",
    banded === "strong" ? "bg-pill" : banded ? "bg-muted" : undefined,
  );
  const empty = !isLoading && rows.length === 0;
  // A row line of a banded table is the hairline; the band and the frame follow the same rule.
  const line = banded ? "border-hairline" : "border-border";

  return (
    <div
      data-density={density}
      className={cn(
        "@container/dt relative min-w-0 rounded-md border border-border bg-card shadow-sm",
        banded ? "rounded-lg border-hairline shadow-none" : undefined,
        className,
      )}
    >
      <div
        ref={scroller}
        onScroll={measure}
        // The box scrolls when the columns or the rows do not fit. A keyboard user must be able to focus it to scroll it (WCAG 2.1.1), and
        // it is then a named region. When nothing scrolls it is a plain box: no tab stop, no landmark.
        {...(scrollable ? { role: "region", tabIndex: 0, "aria-labelledby": captionId } : {})}
        className={cn(
          // `relative`: a visually hidden label (`sr-only` is `position: absolute`) must scroll with the box. Without a positioned box it
          // sticks to the frame and widens the page.
          "relative overflow-x-auto overflow-y-auto rounded-[inherit] @max-[720px]/dt:overflow-visible",
          stickyHeader ? maxHeightClassName : undefined,
          stickyHeader ? "@max-[720px]/dt:max-h-none" : undefined,
        )}
      >
        <table
          {...roles.table}
          aria-busy={isLoading ? true : undefined}
          className="w-full border-collapse text-left text-sm @max-[720px]/dt:block"
        >
          <caption
            id={captionId}
            className={
              captionVisible
                ? "border-b border-hairline px-4 py-3 text-left text-base font-semibold"
                : "sr-only"
            }
          >
            {caption}
          </caption>
          <thead {...roles.rowgroup} className="@max-[720px]/dt:sr-only">
            {table.getHeaderGroups().map((group) => (
              <tr
                key={group.id}
                {...roles.row}
                className={cn(rowClasses[density], "border-b", line)}
              >
                {group.headers.map((header) => {
                  const column = header.column;
                  const canSort = sortable && column.getCanSort();
                  const sorted = column.getIsSorted();
                  const meta = column.columnDef.meta;
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      {...roles.columnheader}
                      aria-sort={
                        canSort
                          ? sorted === "asc"
                            ? "ascending"
                            : sorted === "desc"
                              ? "descending"
                              : "none"
                          : undefined
                      }
                      className={cn(
                        // `text-left`: a `th` is centred by the browser, and the table's own `text-left` does not reach it.
                        "bg-card px-0 text-left font-semibold whitespace-nowrap text-muted-foreground",
                        headClasses,
                        stickyHeader ? "sticky top-0 z-[var(--lf-z-raised)]" : undefined,
                        !canSort ? cell : undefined,
                        meta?.headClassName,
                      )}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={column.getToggleSortingHandler()}
                          className={cn(
                            "inline-flex w-full cursor-pointer items-center gap-1.5 text-left font-semibold hover:text-foreground",
                            headClasses,
                            cell,
                            density === "compact" ? "min-h-8" : "min-h-11",
                          )}
                        >
                          {flexRender(column.columnDef.header, header.getContext())}
                          <span aria-hidden="true" className="inline-flex">
                            {sorted === "asc" ? (
                              <Icon name="arrow-up-01-round" className="size-3.5" />
                            ) : sorted === "desc" ? (
                              <Icon name="arrow-down-01-round" className="size-3.5" />
                            ) : (
                              <Icon name="unfold-more-round" className="size-3.5 opacity-60" />
                            )}
                          </span>
                        </button>
                      ) : (
                        flexRender(column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody
            {...roles.rowgroup}
            className="@max-[720px]/dt:grid @max-[720px]/dt:gap-3 @max-[720px]/dt:p-3"
          >
            {isLoading ? (
              <>
                <tr {...roles.row}>
                  <td {...roles.cell} colSpan={visibleColumns.length} className="sr-only">
                    <span role="status">Loading rows</span>
                  </td>
                </tr>
                {Array.from({ length: loadingRows }, (_, index) => (
                  <tr
                    // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no identity
                    key={index}
                    {...skeletonRow}
                    className={cn(
                      rowClasses[density],
                      "border-b last:border-b-0",
                      line,
                      "@max-[720px]/dt:block @max-[720px]/dt:h-auto @max-[720px]/dt:rounded-sm @max-[720px]/dt:border",
                    )}
                  >
                    {visibleColumns.map((column) => (
                      <td
                        key={column.id}
                        {...roles.cell}
                        className={cn(cell, "@max-[720px]/dt:block")}
                      >
                        <Skeleton className="h-4 w-4/5" />
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            ) : empty ? (
              <tr {...roles.row}>
                <td {...roles.cell} colSpan={Math.max(visibleColumns.length, 1)} className="p-0">
                  {emptyState ?? <EmptyState title="Nothing to show" />}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const detail = renderRowDetail?.(row.original) ?? null;
                return (
                  <Fragment key={row.id}>
                    <tr
                      {...roles.row}
                      {...rowAttributes?.(row.original)}
                      onClick={onRowClick ? (event) => handleRowClick(event, row) : undefined}
                      className={cn(
                        rowClasses[density],
                        "border-b last:border-b-0 hover:bg-muted",
                        line,
                        onRowClick ? "cursor-pointer" : undefined,
                        rowClassName?.(row.original),
                        "@max-[720px]/dt:block @max-[720px]/dt:h-auto @max-[720px]/dt:rounded-sm @max-[720px]/dt:border @max-[720px]/dt:last:border-b",
                      )}
                    >
                      {row.getVisibleCells().map((cellInstance) => {
                        const column = cellInstance.column;
                        const CellTag = column.columnDef.meta?.rowHeader ? "th" : "td";
                        return (
                          <CellTag
                            key={cellInstance.id}
                            {...(CellTag === "th" ? roles.rowheader : roles.cell)}
                            {...(CellTag === "th" ? { scope: "row" } : {})}
                            data-label={headerLabel(column)}
                            className={cn(
                              cell,
                              "align-middle",
                              CellTag === "th" ? "text-left font-medium" : undefined,
                              "@max-[720px]/dt:flex @max-[720px]/dt:items-baseline @max-[720px]/dt:justify-between @max-[720px]/dt:gap-4 @max-[720px]/dt:text-right before:@max-[720px]/dt:content-[attr(data-label)] before:@max-[720px]/dt:supports-[content:'x'_/_'']:content-[attr(data-label)_/_''] before:@max-[720px]/dt:text-left before:@max-[720px]/dt:text-xs before:@max-[720px]/dt:font-semibold before:@max-[720px]/dt:tracking-wide before:@max-[720px]/dt:text-muted-foreground before:@max-[720px]/dt:uppercase",
                              column.columnDef.meta?.className,
                            )}
                          >
                            {flexRender(column.columnDef.cell, cellInstance.getContext())}
                          </CellTag>
                        );
                      })}
                    </tr>
                    {detail === null ? null : (
                      <tr
                        {...roles.row}
                        className={cn(
                          "border-b last:border-b-0 @max-[720px]/dt:block @max-[720px]/dt:rounded-sm @max-[720px]/dt:border",
                          line,
                        )}
                      >
                        <td
                          {...roles.cell}
                          id={detail.id}
                          colSpan={Math.max(visibleColumns.length, 1)}
                          className="bg-accent px-5 py-3 @max-[720px]/dt:block"
                        >
                          {detail.content}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {overflow.right ? (
        <div
          aria-hidden="true"
          data-testid="scroll-hint-right"
          className="pointer-events-none absolute inset-y-0 right-0 w-px rounded-r-md bg-input @max-[720px]/dt:hidden"
        />
      ) : null}
      {overflow.left ? (
        <div
          aria-hidden="true"
          data-testid="scroll-hint-left"
          className="pointer-events-none absolute inset-y-0 left-0 w-px rounded-l-md bg-input @max-[720px]/dt:hidden"
        />
      ) : null}
    </div>
  );
}

export type { ColumnDef, SortingState, VisibilityState };
